import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import * as Sentry from '@sentry/browser';
import { motion } from 'framer-motion';
import { ArrowUp, PenLine, Maximize, Minimize, PanelLeft, X } from 'lucide-react';
//child components
import WordByWord from './WordByWord';
import ProcessProgress from 'components/process-progress';
import MessageActions from 'components/message-actions';
//constants
import { api as chat_end_point } from 'constants/constant.js';
import { hasPermissions } from 'constants/permissionChecker';
import { withoutEarlyRefreshed } from 'constants/aiToolInvalidationMap';
//libs
import { Resizable } from 're-resizable';
//styles
import styles from '@/styles/features/loan-information/chat-model/ChatModel.module.css';
import messageStyles from '@/styles/features/loan-information/chat-model/ChatMessage.module.css';
import inputStyles from '@/styles/features/loan-information/chat-model/ChatInput.module.css';
//utils
import { token } from 'auth/auth';
import tokenManager from 'services/auth/tokenManager';
//icons
import selectDocumentIcon from 'assets/icons/selectDocument.svg';
import userIcon from 'assets/icons/user.svg';
import { useUploadChatDocsMutation } from 'redux/services/client/documentUpload';
import crosspointDummy from 'assets/CrosspointTop.svg';
import { useSpeechToText } from 'components/SpeechToText/SpeechToText';
import VoiceLevelIndicator from 'components/SpeechToText/VoiceLevelIndicator';
import AudioPlayer from 'components/SpeechToText/AudioPlayer';
import 'components/SpeechToText/SpeechToText.css';
//RTK
import { useSelector, useDispatch } from 'react-redux';
import { chatbotData } from 'redux/slices/chatMessage';
import { claimQueuedRequest, setChatMessage } from 'redux/slices/chatMessage';
import { refreshProfileAfterAiWrite } from 'redux/slices/aiProfileSync';
import CocPlaybook from 'components/coc-playbook';
import { setPageInfo } from 'redux/slices/application';
import { chatQuery, removeMarkersAndJson } from 'constants/chatQuery';
import { getUserDetails } from 'auth/auth';
//components
import {
  useGetUserDetailsQuery,
  useListUsersQuery,
} from 'redux/services/admin/userManagementV2';
//components
import Loader from 'wrapper/loader';
//redux
import { userRoleData } from 'redux/slices/userRole';
import ChatHistory from './ChatHistory';
import { ChatHistorySkeleton, ChatDayDivider } from '@/components/mira';
import { buildMessageItems, formatMessageTime } from 'utils/chatDayDivider';
import {
  borrowerProfileAPI,
  useCreateNewChatMutation,
  useGetAllChatsQuery,
  // The B2B route, not the consumer one. This panel is B2B and had been calling
  // `b2c/loan/dashboard/borrower_profile`, which worked only for as long as the
  // consumer routers were mounted.
  useGetBorrowerProfileQuery,
} from 'redux/services/borrower-profile';
import { chatAPI } from 'redux/services/chatHistory';

const TABLET_CHAT_RAIL_WIDTH = 60;

/* 360 docked, 324 on a compact desktop.

   Nothing about the panel's own contents wanted 420 — it was the width it
   happened to be built at, and on a 14" laptop it took a quarter of the screen
   away from the page it sits beside. Messages still set comfortably at 360.

   The alignment of the panel's edge with its history toggle is not a function
   of this number: the toggle is the first child of a header whose padding used
   to inset it, so the two are flush at any width (see ChatModel.module.css).

   The compact step is for viewports under 1512, where the page cannot spare
   the same 360 — a 15" MacBook Air at its default 1470 leaves the pipeline
   1018px at 324, against the 990 its seven columns want. */
const COMPACT_DESKTOP_MEDIA_QUERY = '(max-width: 1511px)';
const DOCKED_CHAT_WIDTH = 360;
const COMPACT_DOCKED_CHAT_WIDTH = 324;
const TABLET_MEDIA_QUERY = '(min-width: 768px) and (max-width: 1366px)';
const ACTIVE_SESSION_KEY = 'mira_active_chat_session';

const ChatModel = ({ docked = false, onClose }) => {
  const { userId, pendingApplicationId, firstName, lastName, userEmail, permissions } =
    getUserDetails();
  const role = useSelector(userRoleData);
  const isConsumer =
    hasPermissions(permissions, 'consumer') ||
    role?.userRole === 'consumer';
  const MiraChevronIcon = () => (
    <div className={messageStyles.botAvatarIcon}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15.9216 19.2561C16.3654 18.8123 16.3654 18.0927 15.9216 17.6489C15.4779 17.2052 14.7585 17.2051 14.3147 17.6486L13.5361 18.4265C12.6586 19.3033 11.2353 19.2989 10.3632 18.4166L9.50818 17.5514C9.08656 17.1249 8.39833 17.1228 7.97422 17.5469C7.55187 17.9693 7.55187 18.6541 7.97422 19.0764L9.69403 20.7962C10.9884 22.0906 13.0871 22.0906 14.3815 20.7962L15.9216 19.2561ZM16.6673 12.0978C15.7979 11.2284 14.3769 11.2398 13.5075 12.1092C12.6506 12.9661 11.25 12.9774 10.3931 12.1204C9.53621 11.2635 8.14687 11.2635 7.28996 12.1204L4.91103 14.4994C4.49298 14.9174 4.49298 15.5952 4.91103 16.0132C5.32907 16.4313 6.00685 16.4313 6.42489 16.0132L7.1944 15.2437C8.03742 14.4007 9.40424 14.4007 10.2473 15.2437L10.3089 15.3053C11.1859 16.1824 12.6198 16.1705 13.4969 15.2934C14.3788 14.4115 15.8206 14.3995 16.7026 15.2814L17.5123 16.0912C17.947 16.5259 18.6518 16.5259 19.0865 16.0912C19.5212 15.6565 19.5212 14.9517 19.0865 14.517L16.6673 12.0978ZM20.4746 12.9252C20.9662 13.4167 21.7632 13.4164 22.2544 12.9245C22.7451 12.4331 22.7449 11.637 22.2538 11.146L14.8913 3.78351C13.3292 2.22141 10.7966 2.22141 9.23447 3.78351L1.72618 11.2918C1.30186 11.7161 1.30186 12.4041 1.72618 12.8284C2.15046 13.2527 2.83834 13.2527 3.26268 12.8285L8.99306 7.09966C10.5552 5.53792 13.0876 5.53809 14.6495 7.10004L20.4746 12.9252Z" fill="#3c588b"/>
      </svg>
    </div>
  );
  const userRole = role?.userRole ?? null;
  // True when Redux role hasn't been hydrated yet — prevents B2B queries from
  // firing (and returning 403) before we know the user is actually B2B.
  const roleNotResolved = !userRole;
  const fileInputRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [inputValue, setInputValue] = useState({
    sender: 'user',
    content: '',
  });
  const chatRequest = useSelector(chatbotData);
  const dispatch = useDispatch();

  const [isVectorClicks, setVectorClicks] = useState(false);
  const [isNewChat, setIsNewChat] = useState(true);
  const [isNewChatOpens, setNewChatOpens] = useState(false);
  const [isNewChatStarted, setNewChatStarted] = useState(false);
  const [chatHistorySelected, setChatHistorySelected] = useState(false);
  const [newChatSelected, setNewChatSelected] = useState(false);
  const [isProfilePicError, setIsProfilePicError] = useState(false);

  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U';

  const {
    isRecording,
    isSpeaking,
    error: speechError,
    startRecording,
    stopRecording,
    setIsRecording,
    setIsSpeaking,
    audioContext,
    mediaStream,
  } = useSpeechToText();

  const [transcriptText, setTranscriptText] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const lastVoiceMessageRef = useRef(null);
  const vadFallbackTimerRef = useRef(null);

  // Speaker button: TTS audio keyed by message id, held in memory for as long
  // as the panel is mounted so a replay costs no round trip and no synthesis.
  // pendingAudioIds holds every message currently waiting on audio (more than
  // one can be in flight — a mic reply synthesizes on its own while the user
  // clicks another message's speaker), activeAudioId is the single one allowed
  // to sound, and playTokens drive play-from-start on each click.
  const [messageAudio, setMessageAudio] = useState({});
  const [pendingAudioIds, setPendingAudioIds] = useState({});
  const [activeAudioId, setActiveAudioId] = useState(null);
  const [audioPlayTokens, setAudioPlayTokens] = useState({});

  // Audio is cached per message, per regenerated version, and per language — a
  // message can hold a clip for each combination and they must never be served
  // for one another. Scoping the key this way also means a clip still in flight
  // when the user switches version or language lands in its own slot instead of
  // playing under what is now on screen. The mic path's response_audio arrives
  // with a bare message id: newest version, the reply's own language.
  const audioKeyFor = (messageKey, version, english) =>
    `${messageKey}:${version ?? 0}:${english ? 'en' : 'orig'}`;
  const normalizeAudioKey = (key) => (key && key.includes(':') ? key : audioKeyFor(key, 0, false));

  const clearPendingAudio = useCallback((messageKey) => {
    setPendingAudioIds((prev) => {
      if (!prev[messageKey]) return prev;
      const next = { ...prev };
      delete next[messageKey];
      return next;
    });
  }, []);

  // A mic turn arrives with synthesis already in flight (`speech_pending`). Spin
  // that message's speaker button straight away and pre-arm its play token, so
  // the clip starts the instant it lands instead of the UI sitting silent for a
  // second. Typed replies carry no flag and are unaffected.
  const armPendingSpeech = useCallback((payload) => {
    // A mic reply is always the newest turn and always speaks in the language it
    // answered in, never English.
    const pendingAudioId = payload?.message_id ? audioKeyFor(payload.message_id, 0, false) : null;
    if (!payload?.speech_pending || !pendingAudioId) return;
    setPendingAudioIds((prev) => ({ ...prev, [pendingAudioId]: true }));
    setActiveAudioId(pendingAudioId);
    setAudioPlayTokens((prev) => ({
      ...prev,
      [pendingAudioId]: (prev[pendingAudioId] || 0) + 1,
    }));
  }, []);

  // Speaker click: replay from memory when we already have the audio, otherwise
  // ask the backend to synthesize this message's spoken variant. The reply comes
  // back on the same socket as `response_audio` carrying this message_id.
  const handlePlaySpeech = useCallback((messageKey, speechText) => {
    if (!messageKey || !speechText) return;

    setActiveAudioId(messageKey);
    setAudioPlayTokens((prev) => ({ ...prev, [messageKey]: (prev[messageKey] || 0) + 1 }));

    if (messageAudio[messageKey]) {
      return; // cached — the token bump replays it, no round trip
    }

    if (socketRef.current?.readyState !== WebSocket.OPEN) {
      console.warn('[chat-model] Cannot synthesize speech — socket not open');
      return;
    }

    setPendingAudioIds((prev) => ({ ...prev, [messageKey]: true }));
    socketRef.current.send(
      JSON.stringify({
        type: 'synthesize',
        session_id: wsSessionIdRef.current,
        message_id: messageKey,
        text: speechText,
      }),
    );
  }, [messageAudio]);

  // VAD auto-send: when recording stops and final transcript arrives,
  // auto-clear transcript buffer (message already added to chat by handleTranscriptMessage)
  useEffect(() => {
    if (!isRecording && transcriptText) {
      clearTimeout(vadFallbackTimerRef.current);
      setIsTyping(true);
      setTranscriptText("");
      setInterimTranscript("");
    }
  }, [isRecording, transcriptText]);

  // The agent closes each language block with its own SPEECH section:
  //   <answer> __SPEECH__ <speech>                          (English-only reply)
  //   <answer> __SPEECH__ <speech>
  //   __ENGLISH__ <english answer> __SPEECH__ <english speech>   (other languages)
  // A wrapped marker (__SPEECH__ / **SPEECH**) is unambiguous, so case is
  // ignored there. Unwrapped, only ALL-CAPS SPEECH counts: the marker is always
  // uppercase while "speech" in prose is not, and matching the ordinary word
  // would truncate the text at it.
  const SPEECH_MARKER_SOURCE = '\\n*(?:[_*]{1,2}[Ss][Pp][Ee][Ee][Cc][Hh][_*]{1,2}|SPEECH)(?::)?\\s+';
  // The wrapped ENGLISH marker matches anywhere; a bare "ENGLISH" only when
  // alone on its own line.
  const ENGLISH_MARKER_REGEX = /\s*[_*]{2}ENGLISH[_*]{2}\s*:?\s*|\n+\s*ENGLISH\s*:?\s*\n+/i;

  // Split one language block into its body and any speech sections it holds.
  const splitSpeechSections = (part) => {
    // No `i` flag — the wrapped form spells its own case-insensitivity, and a
    // case-insensitive bare alternative would match the word "speech".
    const regex = new RegExp(SPEECH_MARKER_SOURCE, 'g');
    const marks = [];
    let match;
    while ((match = regex.exec(part)) !== null) {
      marks.push({ start: match.index, end: regex.lastIndex });
      if (match.index === regex.lastIndex) regex.lastIndex += 1; // zero-length guard
    }
    if (marks.length === 0) return { body: part.trim(), sections: [] };
    const body = part.slice(0, marks[0].start).trim();
    const sections = marks
      .map((mark, i) =>
        part.slice(mark.end, i + 1 < marks.length ? marks[i + 1].start : part.length).trim(),
      )
      .filter(Boolean);
    return { body, sections };
  };

  // Split a raw AI reply into its display halves and both spoken variants.
  // Cut on __ENGLISH__ FIRST, then each half on its own __SPEECH__ — cutting on
  // speech first swallows the English marker and the language toggle disappears.
  // English-only replies carry no English marker, so englishText stays null and
  // the toggle stays hidden.
  const parseMessageParts = (raw) => {
    if (!raw) {
      return { originalText: '', englishText: null, speechText: null, englishSpeechText: null };
    }
    const englishMatch = raw.match(ENGLISH_MARKER_REGEX);
    const primaryRaw = englishMatch ? raw.slice(0, englishMatch.index) : raw;
    const englishRaw = englishMatch
      ? raw.slice(englishMatch.index + englishMatch[0].length)
      : null;

    const primary = splitSpeechSections(primaryRaw);
    const english = englishRaw !== null ? splitSpeechSections(englishRaw) : { body: null, sections: [] };

    let speechText = primary.sections[0] || null;
    let englishSpeechText = english.sections.length
      ? english.sections[english.sections.length - 1]
      : null;
    // Recovery for the layout the model most plausibly gets wrong: both speech
    // sections stacked at the end of the English block instead of one per block.
    if (!speechText && english.sections.length >= 2) {
      speechText = english.sections[0];
    }
    // Messages written before the two-block layout carry a single speech — it
    // serves both languages rather than leaving one view without a button.
    if (!speechText) speechText = englishSpeechText;
    if (!englishSpeechText) englishSpeechText = speechText;

    return {
      originalText: primary.body,
      englishText: english.body,
      speechText,
      englishSpeechText,
    };
  };

  // Handler for WebSocket messages including interim transcripts
  const handleTranscriptMessage = (message) => {
    const messageType = message?.type || message?.response_type;
    const data = message?.data || message || {};

    console.log("Transcript message received:", messageType, data);

    if (messageType === "interim_transcript") {
      // Only update interim transcript if we're still recording
      if (isRecording) {
        // Clean any "USING MIC:" prefix from the transcript
        const cleanTranscript = (data.message || data.transcript || "")
          .replace(/^USING MIC:\s*/i, "");
        setInterimTranscript(cleanTranscript);
      }
    } else if (messageType === "user_transcript" || messageType === "transcript") {
      // When we get the final transcript
      const transcriptValue = data.message || data.transcript || "";
      const isFinal = data.is_final ?? true;
      console.log("Final transcript received:", transcriptValue, "is_final:", isFinal);

      // Only process if it's a meaningful message
      if (transcriptValue && transcriptValue.trim() !== "") {
        // Clean any "USING MIC:" prefix from the transcript
        const cleanTranscript = transcriptValue.replace(/^USING MIC:\s*/i, "");

        // Store the complete transcript (this is what will be sent when recording stops)
        setTranscriptText(cleanTranscript);

        // If it's final, clear the interim transcript to avoid confusion
        if (isFinal) {
          setInterimTranscript("");

          // If we're not recording anymore, this means this is the final transcript from the backend
          // Show the thinking state as we wait for the AI to respond
          if (!isRecording) {
            setIsTyping(true);
          }
        }

        if (isFinal && cleanTranscript && lastVoiceMessageRef.current !== cleanTranscript) {
          setMessages((prev) => [
            ...prev,
            {
              sender: 'user',
              content: cleanTranscript,
              created_at: new Date().toISOString(),
            },
          ]);
          setIsTypeWriterActive((prev) => prev + 1);
          lastVoiceMessageRef.current = cleanTranscript;

          // Fallback auto-stop: if VAD silence detection hasn't fired after 2s, force stop
          clearTimeout(vadFallbackTimerRef.current);
          vadFallbackTimerRef.current = setTimeout(() => {
            if (isRecording) {
              console.log('[VAD fallback] Auto-stopping recording after final transcript');
              stopRecording(socketRef.current, { sessionId: wsSessionIdRef.current, force: true });
            }
          }, 2000);
        }
      }
    }
  };

  const buildStartPayloadMessage = (sessionId = null) => {
    const startPayload = {
      type: 'start',
      session_id: sessionId || null,
    };

    if (firstName) {
      startPayload.user_name = firstName;
    }
    if (userId) {
      startPayload.user_id = userId;
    }
    if (applicationId) {
      startPayload.application_id = applicationId;
    }
    if (organizationId) {
      startPayload.tenant_id = organizationId;
    }

    return startPayload;
  };

  const handleToggleRecording = async () => {
    if (isRecording) {
      console.log("Stopping recording...");
      stopRecording(socketRef.current, { sessionId: wsSessionIdRef.current });
      // Set the thinking state immediately, as the backend is now processing.
      if (transcriptText.trim() !== "" || interimTranscript.trim() !== "") {
        setIsTyping(true);
      }
    } else {
      if (isTyping) {
        // Block new recordings while the assistant is still replying
        return;
      }
      // Clear any existing transcript when starting a new recording
      setTranscriptText("");
      setInterimTranscript("");
      lastVoiceMessageRef.current = null;
      try {
        if (!wsSessionIdRef.current) {
          if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            console.log("Requesting session before starting recording");
            const startPayload = buildStartPayloadMessage(null);
            socketRef.current.send(JSON.stringify(startPayload));
          } else {
            console.warn("Cannot start recording – WebSocket connection is not ready.");
          }
          return;
        }
        // await connectWebSocket(); // WebSocket is now managed by a useEffect
        startRecording(socketRef.current, {
          sessionId: wsSessionIdRef.current,
        });
      } catch (error) {
        console.error("Failed to connect WebSocket for recording:", error);
      }
    }
  };

  const handleSendTranscript = () => {
    // Always call stopRecording to ensure the UI state is updated correctly.
    // The hook itself has internal checks to prevent issues.
    stopRecording(socketRef.current, { sessionId: wsSessionIdRef.current, force: true, skipSocket: true });

    if (isTyping) {
      // Block voice send while the assistant is still replying
      setTranscriptText("");
      setInterimTranscript("");
      return;
    }

    // Use the final transcript if available, otherwise fall back to the last interim one.
    const textToSend = transcriptText || interimTranscript;

    if (textToSend) {
      if (lastVoiceMessageRef.current !== textToSend) {
        const outgoingMessage = {
          sender: 'user',
          content: textToSend,
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, outgoingMessage]);
        setIsTypeWriterActive((prev) => prev + 1);
        lastVoiceMessageRef.current = textToSend;
      }

      if (!isTyping) {
        // Show the thinking UI while the backend processes the captured audio
        setIsTyping(true);
      }
    }

    // Clear the local transcript buffers; the backend will deliver the response.
    setTranscriptText("");
    setInterimTranscript("");
  };

  const [isTyping, setIsTyping] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isTypewriterActive, setIsTypeWriterActive] = useState(0);
  const socketRef = useRef(null);
  /* The answer being generated right now, if any. A pre-underwriting run takes
     minutes, which is long enough for the socket to drop and reconnect while it
     is still working. The task id outlives the socket that started it, so the
     new one can re-attach to the same task instead of leaving the user with a
     spinner that never resolves and an answer delivered to nobody. */
  const pendingTaskRef = useRef(null);
  /* message_id → { toolName: count } of writes already reloaded when their
     tool_completed frame arrived, so the reply does not reload them again. */
  const earlyRefreshedRef = useRef({});
  /* A turn's reply has landed: reload whatever it wrote that no
     tool_completed frame already reloaded, then forget the turn. */
  const refreshUnrefreshedWrites = useCallback((messageId, functionCalls) => {
    const key = messageId || '';
    const remaining = withoutEarlyRefreshed(functionCalls, earlyRefreshedRef.current[key]);
    delete earlyRefreshedRef.current[key];
    dispatch(
      refreshProfileAfterAiWrite(
        remaining.map((fc) => ({ name: fc?.name, displayId: fc?.args?.display_id }))
      )
    );
  }, [dispatch]);
  const messageQueueRef = useRef([]);
  const retriesRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const intentionalCloseRef = useRef(false);
  const MAX_RECONNECT_RETRIES = 10;
  const BASE_RECONNECT_DELAY = 1000;
  const MAX_RECONNECT_DELAY = 30000;
  const [isMessageStreaming, setIsMessageStreaming] = useState(false);
  const [isDocumentUploading, setIsDocumentUploading] = useState(false);
  const [chatUploadProgress, setChatUploadProgress] = useState(null);
  const [isConnectionOpen, setIsConnectionOpened] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [organizationId, setOrganizationId] = useState(null);
  const messageStreamRef = useRef(false);
  const persistedSessionId = sessionStorage.getItem(ACTIVE_SESSION_KEY) || null;
  const chatId = useRef(persistedSessionId);
  const wsSessionIdRef = useRef(persistedSessionId);
  const regeneratingRef = useRef(null);
  const [regeneratingMsg, setRegeneratingMsg] = useState(null);
  const isConnectedRef = useRef(false);
  const chatPromptRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const assistantIdRef = useRef('');
  const threadIdRef = useRef('');
  const [resizedWidth, setResizedWidth] = useState(55);
  const [isMaximized, setIsMaximized] = useState(false);
  const connectionRef = useRef();
  const handleSendMessageRef = useRef(null);
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [isTabletChatHidden, setIsTabletChatHidden] = useState(false);
  const shouldShiftForHistory = !docked && isVectorClicks && isNewChatOpens && !newChatSelected;
  const [isTabletViewport, setIsTabletViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(TABLET_MEDIA_QUERY).matches;
  });
  const [isCompactDesktop, setIsCompactDesktop] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(COMPACT_DESKTOP_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mql = window.matchMedia(TABLET_MEDIA_QUERY);
    const handler = (event) => {
      setIsTabletViewport(event.matches);
      if (!event.matches) {
        setIsTabletChatHidden(false);
      }
    };

    setIsTabletViewport(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mql = window.matchMedia(COMPACT_DESKTOP_MEDIA_QUERY);
    const handler = (event) => setIsCompactDesktop(event.matches);

    setIsCompactDesktop(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!docked) {
      return;
    }
    // Prevent stale HMR-preserved history layout state from leaving left-side artifacts.
    setVectorClicks(false);
    setNewChatOpens(false);
    setNewChatSelected(false);
  }, [docked]);

  useEffect(() => {
    if (!docked) {
      return;
    }

    const root = document.documentElement;
    // 404, not 420. The panel's own header carries `padding: 0 1rem`, so its
    // first control — the history toggle — starts 16px inside the edge. At 420
    // the panel edge fell 16px left of that button, close enough to read as a
    // misalignment against the top bar rather than as deliberate inset. Taking
    // those 16px off lines the edge up with the button and hands them back to
    // the page, which on a 1512px laptop is the difference between the pipeline
    // table fitting and scrolling.
    const dockedWidth = isCompactDesktop
      ? COMPACT_DOCKED_CHAT_WIDTH
      : DOCKED_CHAT_WIDTH;
    root.style.setProperty('--chat-aside-width', `${dockedWidth}px`);
    root.style.setProperty('--chat-aside-expanded-width', '560px');

    if (isTabletViewport && isTabletChatHidden) {
      root.style.setProperty('--chat-aside-current-width', `${TABLET_CHAT_RAIL_WIDTH}px`);
      return;
    }

    const targetWidth = isChatExpanded
      ? 'var(--chat-aside-expanded-width)'
      : 'var(--chat-aside-width)';

    root.style.setProperty('--chat-aside-current-width', targetWidth);
  }, [docked, isChatExpanded, isTabletViewport, isTabletChatHidden, isCompactDesktop]);

  const playBase64Audio = (audioBase64, mimeType = 'audio/mpeg') => {
    if (!audioBase64) {
      return;
    }

    try {
      const audioData = atob(audioBase64);
      const audioBytes = new Uint8Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        audioBytes[i] = audioData.charCodeAt(i);
      }
      const audioBlob = new Blob([audioBytes], {
        type: mimeType,
      });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.play().catch((error) => {
        console.error('Failed to play synthesized audio:', error);
      });
    } catch (error) {
      console.error('Failed to decode or play audio:', error);
    }
  };

  const buildWebSocketUrl = (userIdValue, tenantIdValue) => {
    const searchParams = new URLSearchParams();

    if (userIdValue) {
      searchParams.append('user_id', String(userIdValue));
    }
    if (tenantIdValue) {
      searchParams.append('tenant_id', String(tenantIdValue));
    }

    // B2C consumers use a dedicated WebSocket endpoint
    const wsPath = isConsumer
      ? '/ws/b2c/ai/chat'
      : '/api/v1/b2b/ai/ws/chat';

    // In production, always use the configured API endpoint so WebSocket reaches
    // the backend (not the CDN). In dev over HTTPS, use the current origin
    // (goes through Vite proxy) to avoid mixed content.
    const isSecure = window.location.protocol === 'https:';
    const useProxy = isSecure && !import.meta.env.PROD;
    const baseUrl = useProxy ? window.location.origin : (chat_end_point || window.location.origin);

    try {
      const url = new URL(baseUrl);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      url.pathname = wsPath;
      url.search = searchParams.toString();
      return url.toString();
    } catch (error) {
      console.warn('[chat-model] Failed to parse API base URL for WebSocket', error);
      const origin = window.location.origin.replace(/^http/, 'ws');
      const query = searchParams.toString();
      return `${origin}${wsPath}${query ? `?${query}` : ''}`;
    }
  };

  const [uploadData, { data, isLoading, error }] = useUploadChatDocsMutation();
  // Use the same RTK endpoint as the dashboard so RTK Query deduplicates
  // the request instead of making two separate calls to the same URL.
  // mode=summary keeps the call fast (~500ms) — ChatModel only needs
  // personal_details + loan_details for chat context.
  const { data: applicationData, refetch: refetchApplicationData } =
    useGetBorrowerProfileQuery(
      applicationId ? { applicationId, mode: 'summary' } : undefined,
      {
        skip: !applicationId,
        refetchOnMountOrArgChange: 300,
      }
    );

  const [messages, setMessages] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const messageItems = useMemo(() => buildMessageItems(messages), [messages]);
  // AI transparency: Track process events per message_id
  const [processStates, setProcessStates] = useState({});
  const [currentMessageId, setCurrentMessageId] = useState(null);

  const clearActiveProcessState = useCallback(() => {
    setProcessStates((prev) => {
      if (!currentMessageId) return prev;
      const updated = { ...prev };
      delete updated[currentMessageId];
      return updated;
    });
    setCurrentMessageId(null);
  }, [currentMessageId]);

  // Debug: Log process state changes
  useEffect(() => {
    console.log('[AI Transparency] processStates updated:', processStates);
    console.log('[AI Transparency] currentMessageId:', currentMessageId);
  }, [processStates, currentMessageId]);

  const activateSession = (sessionId, { broadcast = false, resetMessages = false } = {}) => {
    const previousSessionId = chatId.current ?? null;
    const nextSessionId = sessionId ?? null;
    chatId.current = nextSessionId;
    wsSessionIdRef.current = nextSessionId;

    // Speaker audio is cached per message for the panel's lifetime, and the panel
    // never unmounts while navigating the app — so without this it would pile up
    // across every conversation opened. Moving to a different session drops it:
    // those messages are off screen, and emptying the map unmounts their players,
    // which revokes the object URLs and actually frees the bytes. Keyed on a real
    // id change so the no-op re-activations on each AI response leave it alone.
    if (previousSessionId !== nextSessionId) {
      setMessageAudio({});
      setAudioPlayTokens({});
      setPendingAudioIds({});
      setActiveAudioId(null);
      // A task belongs to the session that started it. Carried across, the
      // session_ready handler below re-attaches to it and the previous chat's
      // answer lands in this one — which is the thing a new chat exists to
      // leave behind. A reconnect re-activates the SAME id, so the resume
      // path is untouched.
      pendingTaskRef.current = null;
    }

    // NOTE: We intentionally do NOT persist to sessionStorage here.
    // The ACTIVE_SESSION_KEY is only set when the user actually interacts
    // (sends a message or receives a response) so that reloads without
    // any chat interaction start with a clean slate.
    if (!sessionId) {
      sessionStorage.removeItem(ACTIVE_SESSION_KEY);
    }

    if (resetMessages) {
      setMessages([]);
    }

    if (broadcast) {
      const socket = socketRef.current;
      if (socket && socket.readyState === WebSocket.OPEN) {
        const startPayload = buildStartPayloadMessage(sessionId);
        console.log('[chat-model] Sending WebSocket start payload:', startPayload);
        try {
          socket.send(JSON.stringify(startPayload));
        } catch (error) {
          console.error('[chat-model] Failed to send WebSocket start payload', error);
        }
      }
    }
  };
  //const [isTextPasted, setIsTextPasted] = useState(false);
  const setInputValueFunction = (text) => {
    const trimmedText = text.trimStart(); // remove leading whitespace
    if (!trimmedText) return;

    setIsTyping(false);
    setIsTypeWriterActive((prev) => prev + 1);
    setInputValue((prevInputValue) => ({
      ...prevInputValue,
      content: trimmedText,
    }));

    setTimeout(() => {
      if (chatPromptRef.current) {
        chatPromptRef.current.style.height = 'auto';
        const height = chatPromptRef.current.scrollHeight;
        chatPromptRef.current.style.height = `${height < 100 ? height : 100}px`;
      }
    }, 0);
  };

  const handleInputChange = (event) => {
    const rawValue = event.target.value;

    if (rawValue.length === 1 && rawValue[0] === ' ') return;

    setInputValue({ ...inputValue, content: rawValue });

    // Auto-resize textarea as user types
    if (chatPromptRef.current) {
      chatPromptRef.current.style.height = 'auto';
      const scrollHeight = chatPromptRef.current.scrollHeight;
      // Max height of 120px (approximately 5 lines)
      chatPromptRef.current.style.height = `${Math.min(scrollHeight, 120)}px`;
    }
  };

  const { data: profileData } = useGetUserDetailsQuery(
    userId,
    {
      skip: !userId || isConsumer || roleNotResolved,
    }
  );

  // Fallback: Fetch user from list API (same as Employee Management) to ensure we get the picture
  const { data: listData } = useListUsersQuery(
    { search: userEmail, limit: 1 },
    {
      skip: !userEmail || isConsumer || roleNotResolved,
    }
  );

  const userProfile = useMemo(() => {
    if (!profileData) {
      return null;
    }

    const candidates = [
      profileData?.user,
      profileData?.data?.user,
      profileData?.data,
      profileData,
    ].filter(Boolean);

    for (const candidate of candidates) {
      if (candidate?.user && typeof candidate.user === 'object') {
        return candidate.user;
      }

      if (typeof candidate === 'object') {
        return candidate;
      }
    }

    return null;
  }, [profileData]);

  /* The register read from ONE turn, for that turn's card to draw each
     record's own account from.
     
     Its own, not the newest on the transcript: every guided turn now keeps a
     card, so a global "newest wins" gave two cards on one loan the same
     narrative — the later turn's — under two different questions. The card
     still checks the Profile ID before showing any of it, so a payload about
     another loan is harmless either way. */
  const cocArtifactOf = useCallback(
    (message) =>
      (message?.artifacts ?? []).find((a) => a?.tool === 'get_coc_register')
        ?.payload ?? null,
    []
  );


  const borrowerIdValue =
    userProfile?.borrowerId ??
    userProfile?.borrower_id ??
    applicationData?.data?.personal_details?.userProfileId ??
    applicationData?.data?.personalDetails?.userProfileId ??
    null;

  const applicationDisplayIdValue =
    applicationData?.data?.displayId ??
    applicationData?.data?.loan_details?.displayId ??
    applicationData?.data?.loanDetails?.displayId ??
    null;

  const borrowerDisplayName = useMemo(() => {
    const personalDetails =
      applicationData?.data?.personal_details ??
      applicationData?.data?.personalDetails ??
      null;

    if (!personalDetails) {
      return null;
    }

    const parts = [
      personalDetails.firstName ?? personalDetails.first_name ?? null,
      personalDetails.middleName ?? personalDetails.middle_name ?? null,
      personalDetails.lastName ?? personalDetails.last_name ?? null,
    ].filter(Boolean);

    const name = parts.join(' ').replace(/\s+/g, ' ').trim();
    return name || null;
  }, [applicationData]);

  const loanSummary = useMemo(() => {
    const loanInfo =
      applicationData?.data?.loan_information ??
      applicationData?.data?.loanInformation ??
      applicationData?.data?.loanDetails ??
      applicationData?.data ??
      null;

    if (!loanInfo || typeof loanInfo !== 'object') {
      if (applicationId) {
        return `Loan ${applicationId}`;
      }
    }

    const loanNumber =
      loanInfo?.loanNumber ??
      loanInfo?.loan_number ??
      loanInfo?.loanNo ??
      loanInfo?.loan_no ??
      null;

    const program =
      loanInfo?.loanProgram ??
      loanInfo?.loan_program ??
      loanInfo?.program ??
      null;

    const loanType =
      loanInfo?.loanType ??
      loanInfo?.loan_type ??
      null;

    if (loanNumber) {
      return `Loan #${loanNumber}`;
    }
    if (program) {
      return program;
    }
    if (loanType) {
      return loanType;
    }
    if (applicationId) {
      return `Loan ${applicationId}`;
    }
    return null;
  }, [applicationData, applicationId]);

  const buildChatMetadata = useCallback(() => {
    const metadata = {
      source: 'chat-ui',
      initiatedAt: new Date().toISOString(),
    };

    if (borrowerIdValue || borrowerDisplayName) {
      const borrowerPayload = {};
      if (borrowerIdValue) {
        borrowerPayload.id = borrowerIdValue;
      }
      if (borrowerDisplayName) {
        borrowerPayload.name = borrowerDisplayName;
      }
      metadata.borrower = borrowerPayload;
    }

    if (applicationId || loanSummary) {
      const loanPayload = {};
      if (applicationId) {
        loanPayload.application_id = applicationId;
      }
      if (applicationDisplayIdValue) {
        loanPayload.display_id = applicationDisplayIdValue;
      }
      if (loanSummary) {
        loanPayload.label = loanSummary;
      }
      metadata.loan = loanPayload;
    }

    return metadata;
  }, [borrowerIdValue, borrowerDisplayName, applicationId, applicationDisplayIdValue, loanSummary]);

  // Get user's profile picture from Azure Blob (same as employee list)
  // Memoized to prevent infinite render loops
  const userProfilePic = useMemo(() => {
    let pic = null;

    // 1. Try getting from detailed profile data
    if (userProfile) {
      pic =
        userProfile?.profile_picture_url ||
        userProfile?.profilePictureUrl ||
        userProfile?.profile_pic_url ||
        userProfile?.avatar_url;
    }

    // 2. If not found, try getting from list data (fallback)
    if (!pic && listData?.users?.length > 0) {
      // Find the user in the list that matches our email or ID
      const listUser = listData.users.find(u =>
        (u.email === userEmail) || (u.id === userId)
      );

      if (listUser) {
        console.log('[Chat] Found user in list data:', listUser);
        pic = listUser.profile_picture_url || listUser.profilePictureUrl;
      }
    }

    console.log('[Chat] Final profile picture:', pic);
    return pic || null;
  }, [profileData, userProfile, listData, userEmail, userId]);

  //RTK query to get all chats
  const {
    data: sessionList,
    refetch: refetchAllChatHistory,
    isLoading: isSessionListLoading,
  } = useGetAllChatsQuery(
    { tenantId: organizationId },
    { skip: isConsumer || roleNotResolved || !userId || !organizationId }
  );

  useEffect(() => {
    const storedUser = tokenManager?.getUserData ? tokenManager.getUserData() : null;
    const storedOrganization =
      storedUser?.primaryOrganizationId ||
      storedUser?.primary_organization_id ||
      storedUser?.organization_id ||
      storedUser?.organizationId ||
      null;

    const profileOrganization =
      userProfile?.primaryOrganizationId ||
      userProfile?.primary_organization_id ||
      userProfile?.organizationId ||
      userProfile?.organization_id ||
      null;

    const resolvedOrganization = storedOrganization || profileOrganization;

    if (resolvedOrganization && resolvedOrganization !== organizationId) {
      setOrganizationId(String(resolvedOrganization));
    }
  }, [userProfile, organizationId]);

  const normalizeSessions = (raw) => {
    if (Array.isArray(raw)) {
      return raw;
    }
    if (raw?.chats && Array.isArray(raw.chats)) {
      return raw.chats;
    }
    if (raw?.data && Array.isArray(raw.data)) {
      return raw.data;
    }
    return [];
  };

  const getSessionId = (session) => {
    if (!session || typeof session !== 'object') {
      return null;
    }
    return session.id || session.chat_id || session.session_id || null;
  };

  const pickLatestSession = (sessions) => {
    if (!sessions?.length) {
      return null;
    }
    const toTimestamp = (session) => {
      const ts =
        session?.last_message_at ||
        session?.metadata?.last_message_at ||
        session?.created_at;
      return ts ? new Date(ts).getTime() : 0;
    };
    return [...sessions].sort((a, b) => toTimestamp(b) - toTimestamp(a))[0];
  };

  const [createNewChat] = useCreateNewChatMutation();
  const hasInitializedChat = useRef(false);
  const isCreatingChat = useRef(false);

  useEffect(() => {
    hasInitializedChat.current = false;
  }, [userId, organizationId, isConsumer]);

  // Transform raw API messages into the shape the chat UI expects
  const transformHistoryMessages = useCallback((rawMessages) => {
    const messageList = rawMessages?.messages ?? (Array.isArray(rawMessages) ? rawMessages : []);
    return messageList.map((message) => {
      const sender = message?.role?.toLowerCase() === 'user' ? 'user' : 'AI';
      let content = message?.content ?? '';
      if (typeof content === 'string') {
        const trimmed = content.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            const maybeMessage = parsed?.data?.message ?? parsed?.message;
            content = typeof maybeMessage === 'string' ? maybeMessage : trimmed;
          } catch (_e) {
            // keep original string
          }
        }
      } else {
        content = JSON.stringify(content);
      }
      // The SPEECH section is deliberately LEFT IN PLACE here. The render strips
      // it for display via parseMessageParts — which matches both __SPEECH__ and
      // **SPEECH** (the model emits either) — and hands the text to the speaker
      // button. Stripping it at this point destroys it before the button can use
      // it, and the button silently disappears on history messages.
      const versions = message?.versions || null;
      return {
        sender,
        content: versions && versions.length ? versions[versions.length - 1] : content,
        isFromHistory: true,
        created_at: message?.created_at,
        id: message?.id,
        feedback: message?.feedback ?? null,
        /* What the turn produced beyond its text, flattened off the stored
           metadata by the query. Leaving these out is why a reload used to turn
           a guided card back into the prose underneath it. */
        artifacts: message?.artifacts || null,
        guidedFlow: message?.guided_flow || null,
        versions,
        activeVersion: versions && versions.length ? versions.length - 1 : 0,
      };
    });
  }, []);

  // Fetch messages for a session via programmatic RTK dispatch
  const fetchSessionMessages = useCallback(async (sessionId) => {
    if (!sessionId || !userId) return [];
    try {
      let result;
      if (isConsumer) {
        result = await dispatch(
          chatAPI.endpoints.getB2cChatMessages.initiate({
            sessionId,
            consumerId: String(userId),
            clientOrgId: organizationId ? String(organizationId) : undefined,
          })
        ).unwrap();
      } else {
        result = await dispatch(
          borrowerProfileAPI.endpoints.getChatMessages.initiate({
            sessionId,
            userId,
          })
        ).unwrap();
      }
      return transformHistoryMessages(result);
    } catch (err) {
      console.warn('[chat-model] Failed to restore messages for session', sessionId, err);
      return [];
    }
  }, [dispatch, userId, organizationId, isConsumer, transformHistoryMessages]);

  const initialCall = async () => {
    // Prevent concurrent initializations (e.g. React StrictMode double-mount)
    if (isCreatingChat.current) return;
    isCreatingChat.current = true;
    try {
      // After a fresh login (sign-out → sign-in), start a brand-new chat
      // instead of resuming the last session.
      const isFreshLogin = sessionStorage.getItem('mira_fresh_login') === 'true';
      if (isFreshLogin) {
        sessionStorage.removeItem('mira_fresh_login');
        sessionStorage.removeItem(ACTIVE_SESSION_KEY);

        if (isConsumer) {
          activateSession(null, { broadcast: true, resetMessages: true });
          return;
        }

        // B2B: create a new session via REST
        try {
          const response = await createNewChat({
            borrower_id: borrowerIdValue,
            loan_application_id: applicationId || userProfile?.applicationId,
            metadata: buildChatMetadata(),
          });
          const newId = response?.data?.id ?? response?.id ?? null;
          const shouldBroadcast =
            socketRef.current && socketRef.current.readyState === WebSocket.OPEN;
          activateSession(newId, { broadcast: !!shouldBroadcast, resetMessages: true });
          if (newId) await refetchAllChatHistory();
        } catch (err) {
          console.warn('[chat-model] Fresh-login session creation failed, falling back to WS', err);
          activateSession(null, { broadcast: true, resetMessages: true });
        }
        return;
      }

      // Only restore a previous session if the user was actively chatting
      // before the reload (indicated by ACTIVE_SESSION_KEY in sessionStorage).
      // Without this guard, pickLatestSession would load old messages from a
      // prior session even when the user never opened the chat.
      const persistedId = sessionStorage.getItem(ACTIVE_SESSION_KEY);

      if (persistedId) {
        const shouldBroadcast =
          socketRef.current && socketRef.current.readyState === WebSocket.OPEN;
        activateSession(persistedId, {
          broadcast: !!shouldBroadcast,
          resetMessages: true,
        });

        // Restore messages from the session so chat persists across reloads
        const restored = await fetchSessionMessages(persistedId);
        if (restored.length > 0) {
          setMessages(restored);
        }
        return;
      }

      // Consumers don't use the REST createNewChat endpoint;
      // the B2C WebSocket auto-creates sessions on connect.
      if (isConsumer) {
        activateSession(null, { broadcast: true, resetMessages: true });
        return;
      }

      // If a session is already selected in memory, don't create a duplicate.
      if (wsSessionIdRef.current) {
        return;
      }

      const response = await createNewChat({
        borrower_id: borrowerIdValue,
        loan_application_id: applicationId || userProfile?.applicationId,
        metadata: buildChatMetadata(),
      });
      const sessionId = response?.data?.id ?? response?.id ?? null;
      if (sessionId) {
        console.log('response chat', response);
        // If the WebSocket auto-created a session while createNewChat was
        // in-flight, keep that session (and any messages the user already sent).
        if (wsSessionIdRef.current) {
          await refetchAllChatHistory();
          return;
        }
        const shouldBroadcast =
          socketRef.current && socketRef.current.readyState === WebSocket.OPEN;
        activateSession(sessionId, { broadcast: !!shouldBroadcast });
        await refetchAllChatHistory();
      } else if (!wsSessionIdRef.current) {
        activateSession(null, { broadcast: true });
      }
    } catch (error) {
      console.error('[chat-model] Failed to initialize chat session', error);
    } finally {
      isCreatingChat.current = false;
    }
  };

  useEffect(() => {
    if (!userId || (!isConsumer && !organizationId)) return;
    // Wait for auto-query to finish loading before initializing chat session
    if (isSessionListLoading || hasInitializedChat.current) return;
    hasInitializedChat.current = true;
    initialCall();
  }, [isSessionListLoading, userId, organizationId, isConsumer]);

  useEffect(() => {
    if (!userId || !organizationId) {
      console.warn('[chat-model] Skipping WebSocket: userId=%s organizationId=%s isConsumer=%s', userId, organizationId, isConsumer);
      return;
    }

    intentionalCloseRef.current = false;

    const connect = () => {
    const websocketUrl = buildWebSocketUrl(userId, organizationId);
    console.log('[chat-model] Connecting WebSocket:', websocketUrl);
    let socket;

    try {
      socket = new WebSocket(websocketUrl);
    } catch (error) {
      console.error('[chat-model] Failed to create WebSocket', error);
      return;
    }

    socketRef.current = socket;

    const sendStartMessage = (sessionId) => {
      activateSession(sessionId, { broadcast: true });
    };

    const handleLegacyPayload = (legacyPayload) => {
      const { response_type: responseType, data } = legacyPayload || {};

      if (!responseType) {
        return;
      }

      switch (responseType) {
        case 'stream_end':
          console.log("Stream ended from backend, sending transcript automatically.");
          handleSendTranscript();
          break;
        case 'speech_event':
          if (data?.event === "SPEECH_ACTIVITY_START") {
            setIsRecording(true);
          }
          break;
        case 'interim_transcript':
        case 'user_transcript':
          handleTranscriptMessage(legacyPayload);
          break;
        case 'ack':
          setIsConnectionOpened(true);
          break;
        case 'chat_created':
          if (data?.chat_id) {
            activateSession(data.chat_id);
          }
          break;
        case 'reasoning':
          if (data?.message) {
            console.debug('[chat-model] AI reasoning event:', data.message);
          }
          break;
        case 'chat_upload_progress':
          if (data?.file_name) {
            const percent = Number.isFinite(data?.progress_percent)
              ? data.progress_percent
              : 0;
            setChatUploadProgress({
              fileName: data.file_name,
              progressPercent: percent,
              status: data.status || 'processing',
            });
            if (data.status === 'completed' || data.status === 'completed_without_mongo' || data.status === 'failed') {
              setIsDocumentUploading(false);
            } else {
              setIsDocumentUploading(true);
            }
          }
          break;
        case 'chat_upload_context': {
          // Document stored in MongoDB — show user bubble and send to AI
          const docName = data.payload?.doc_name || 'document';
          setMessages((prev) => [
            ...prev,
            { sender: 'user', content: `Chat upload Request - "${docName}"`, created_at: new Date().toISOString() },
          ]);

          const socket = socketRef.current;
          if (socket && socket.readyState === WebSocket.OPEN) {
            const wsPayload = isConsumer
              ? {
                message: `Chat upload Request - "${docName}"`,
                chat_id: wsSessionIdRef.current || null,
                file_name: docName,
                metadata: {
                  payload_type: 'document_upload',
                  consumer_user_id: String(userId),
                  organization_id: String(organizationId),
                  document_id: data.payload?.document_id,
                  mongo_document_id: data.payload?.mongo_document_id,
                },
              }
              : {
                type: 'text',
                message: `Chat upload Request - "${docName}"`,
                session_id: wsSessionIdRef.current || null,
                user_id: userId,
                organization_id: organizationId,
                file_name: docName,
                metadata: {
                  payload_type: 'document_upload',
                  document_id: data.payload?.document_id,
                  mongo_document_id: data.payload?.mongo_document_id,
                },
              };
            socket.send(JSON.stringify(wsPayload));
            // Mirror handleSendMessage: turn on the "thinking" state so the
            // ProcessProgress pill renders while the agent analyzes the upload.
            // The backend emits process_start/process_step for this text message,
            // which populates currentMessageId + processStates; the render gate at
            // the bottom also requires isTyping, which this send path never set.
            setIsTyping(true);
          }
          setChatUploadProgress(null);
          setIsDocumentUploading(false);
          break;
        }
        // AI Transparency: Process visibility events
        case 'process_start':
          if (data?.message_id) {
            setCurrentMessageId(data.message_id);
            setProcessStates((prev) => ({
              ...prev,
              [data.message_id]: {
                status: 'active',
                steps: data.steps || [],
                messageId: data.message_id,
              },
            }));
          }
          break;
        case 'tool_completed':
          // A tool MIRA called has finished and its writes are committed.
          // Reload them now, while the reply is still being written, and
          // count it so the reply skips this one. Counted per call: a turn
          // can call application_updates more than once.
          if (legacyPayload?.tool) {
            const key = legacyPayload.message_id || '';
            const counts = earlyRefreshedRef.current[key] || {};
            counts[legacyPayload.tool] = (counts[legacyPayload.tool] || 0) + 1;
            earlyRefreshedRef.current[key] = counts;
            dispatch(
              refreshProfileAfterAiWrite([
                { name: legacyPayload.tool, displayId: legacyPayload.display_id },
              ])
            );
          }
          break;
        case 'process_step':
          if (data?.message_id && data?.step_id) {
            setProcessStates((prev) => {
              const current = prev[data.message_id] || { steps: [], status: 'active' };
              const stepIndex = current.steps.findIndex((s) => s.id === data.step_id);

              const updatedSteps = [...current.steps];
              if (stepIndex >= 0) {
                updatedSteps[stepIndex] = {
                  ...updatedSteps[stepIndex],
                  status: data.status,
                  label: data.label || updatedSteps[stepIndex].label,
                  metadata: data.metadata,
                };
              } else {
                updatedSteps.push({
                  id: data.step_id,
                  status: data.status,
                  label: data.label,
                  metadata: data.metadata,
                });
              }

              return {
                ...prev,
                [data.message_id]: {
                  ...current,
                  steps: updatedSteps,
                },
              };
            });
          }
          break;
        case 'process_complete':
          console.log('[AI Transparency] process_complete received:', data);
          if (data?.message_id) {
            setProcessStates((prev) => ({
              ...prev,
              [data.message_id]: {
                ...prev[data.message_id],
                status: 'complete',
              },
            }));
          }
          window.dispatchEvent(new Event('process-complete'));
          break;
        case 'process_error':
          if (data?.message_id) {
            setProcessStates((prev) => ({
              ...prev,
              [data.message_id]: {
                ...prev[data.message_id],
                status: 'error',
                error: {
                  code: data.error_code,
                  message: data.error_message,
                  stepId: data.step_id,
                },
              },
            }));
          }
          break;
        case 'process_abort':
          if (data?.message_id) {
            setProcessStates((prev) => ({
              ...prev,
              [data.message_id]: {
                ...prev[data.message_id],
                status: 'aborted',
                abortReason: data.reason,
              },
            }));
          }
          break;
        case 'AI_response':
          console.log('[AI Transparency] AI_response received, full payload:', legacyPayload);
          // Backend sends message directly in payload, not in 'data' property
          if (legacyPayload?.message) {
            // The answer landed — there is nothing left to re-attach to. Every
            // reply arrives HERE, not in the typed `chat_response` case: the
            // backend stamps both `type` and `response_type`, and onmessage
            // routes on response_type, so that case never runs for an answer.
            pendingTaskRef.current = null;
            if (legacyPayload?.session_id) {
              activateSession(legacyPayload.session_id);
            }
            console.log('[AI Transparency] Adding AI message to messages array, message_id:', legacyPayload.message_id);

            // Clear process state for this message (so ProcessProgress disappears)
            if (legacyPayload.message_id) {
              setProcessStates((prev) => {
                const updated = { ...prev };
                delete updated[legacyPayload.message_id];
                return updated;
              });
              setCurrentMessageId(null);
            }

            const regenTarget = regeneratingRef.current;
            regeneratingRef.current = null;
            setRegeneratingMsg(null);
            setMessages((prev) => {
              if (regenTarget) {
                const tIdx = prev.findIndex((m) => m === regenTarget);
                if (tIdx >= 0) {
                  const existing = prev[tIdx];
                  const versions = existing.versions || [existing.content];
                  // Each regeneration has its own spoken variant, so the speech
                  // list must stay index-aligned with the version list. A message
                  // restored from history arrives with versions but no speech
                  // list, so pad with nulls and let the render recover those from
                  // the raw text; only the newest version has a known field.
                  const priorSpeech = existing.speechVersions || [];
                  const speechVersions = versions.map((_version, index) =>
                    priorSpeech[index] ??
                    (index === versions.length - 1
                      ? { speech: existing.speechText || null, english: existing.englishSpeechText || null }
                      : null),
                  );
                  const updated = [...prev];
                  updated[tIdx] = {
                    ...existing,
                    versions: [...versions, legacyPayload.message],
                    speechVersions: [
                      ...speechVersions,
                      {
                        speech: legacyPayload.speech_text || null,
                        english: legacyPayload.speech_text_en || null,
                      },
                    ],
                    activeVersion: versions.length,
                    content: legacyPayload.message,
                    speechText: legacyPayload.speech_text || null,
                    englishSpeechText: legacyPayload.speech_text_en || null,
                  };
                  return updated;
                }
              }
              return [
                ...prev,
                {
                  sender: 'ai',
                  content: legacyPayload.message,
                  messageId: legacyPayload.message_id,
                  // Spoken variants for the speaker button, one per language;
                  // the backend has already stripped both out of `message`.
                  speechText: legacyPayload.speech_text || null,
                  englishSpeechText: legacyPayload.speech_text_en || null,
                  // Tool results the turn produced, for surfaces outside the chat
                  // bubble to render. Absent on most turns.
                  artifacts: legacyPayload.metadata?.artifacts || null,
                  // The guided flow this turn belongs to, as the server recorded
                  // it. Read from the reply rather than remembered locally, so
                  // the live message and the same message reloaded next week
                  // render from one source.
                  guidedFlow: legacyPayload.metadata?.guided_flow || null,
                  created_at: new Date().toISOString(),
                },
              ];
            });
            setIsTyping(false);
            setIsTypeWriterActive((prev) => prev + 1);
            armPendingSpeech(legacyPayload);
            // Reload what the turn wrote — the safety net for any write whose
            // tool_completed frame never arrived. Those that did are skipped.
            refreshUnrefreshedWrites(legacyPayload.message_id, legacyPayload.metadata?.function_calls);
            // Refresh the chat-list ~2.5s after the assistant reply so the
            // background-generated session title appears in the sidebar
            // without a page reload.
            setTimeout(() => {
              dispatch(chatAPI.util.invalidateTags(['ChatSessions']));
            }, 2500);
          }
          break;
        case 'error':
          if (data?.message) {
            console.error('[chat-model] WebSocket error:', data.message);
            setMessages((prev) => [
              ...prev,
              {
                sender: 'system',
                content: data.message,
                created_at: new Date().toISOString(),
              },
            ]);
          }
          clearActiveProcessState();
          setIsTyping(false);
          break;
        default:
          break;
      }
    };

    socket.onopen = () => {
      console.log('[chat-model] WebSocket connected successfully');
      retriesRef.current = 0;
      setIsConnectionOpened(true);
      if (isConsumer) {
        // B2C handshake: send consumer_id + client_org_id as the first message
        const handshake = {
          consumer_id: userId,
          client_org_id: organizationId,
        };
        if (wsSessionIdRef.current) {
          handshake.chat_id = wsSessionIdRef.current;
          handshake.restore_session_id = wsSessionIdRef.current;
        }
        try {
          socket.send(JSON.stringify(handshake));
        } catch (err) {
          console.error('[chat-model] Failed to send B2C handshake', err);
        }
      } else if (wsSessionIdRef.current) {
        sendStartMessage(wsSessionIdRef.current);
      }
      // Flush any messages queued while socket was connecting
      while (messageQueueRef.current.length > 0) {
        const queued = messageQueueRef.current.shift();
        try { socket.send(queued); } catch (e) { console.error('[chat-model] Queue flush failed', e); break; }
      }
    };

    socket.onmessage = (event) => {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch (error) {
        console.warn('[chat-model] Received non-JSON WebSocket message', event.data);
        return;
      }

      if (payload?.response_type) {
        handleLegacyPayload(payload);
        return;
      }

      const messageType = payload?.type;

      switch (messageType) {
        case 'ping':
          // Respond to server heartbeat
          if (socket.readyState === WebSocket.OPEN) {
            try { socket.send(JSON.stringify({ type: 'pong', ts: payload.ts })); } catch (_) {}
          }
          return;
        case 'session_ready':
          if (payload.session_id) {
            wsSessionIdRef.current = payload.session_id;
            // Don't persist to sessionStorage here — wait until the user
            // actually sends a message so reloads without interaction stay clean.
          }
          setIsConnectionOpened(true);
          /* Reconnected while an answer was still being generated: ask the new
             socket to follow the task the old one was watching. The server
             replies with the answer when it lands, or task_unavailable when the
             id no longer resolves. */
          if (pendingTaskRef.current?.taskId) {
            try {
              socket.send(
                JSON.stringify({
                  type: 'resume_task',
                  task_id: pendingTaskRef.current.taskId,
                  message_id: pendingTaskRef.current.messageId,
                  session_id: wsSessionIdRef.current,
                })
              );
            } catch (_) {
              /* the socket closed again between open and send — the next
                 reconnect will try once more */
            }
          }
          break;
        /* Acknowledgement and keepalives for an answer in flight. The only
           thing worth keeping is the task id, which is what makes a reconnect
           recoverable. */
        case 'ai_processing':
          if (payload.task_id) {
            pendingTaskRef.current = {
              taskId: payload.task_id,
              messageId: payload.message_id || null,
            };
          }
          setIsTyping(true);
          return;
        /* The task id no longer resolves — a restarted worker, or a result that
           has expired. Nothing is coming, so stop waiting for it. */
        case 'task_unavailable':
          console.warn('[chat-model] Task no longer available: %s', payload.task_id);
          pendingTaskRef.current = null;
          setIsTyping(false);
          return;
        case 'session_restored': {
          // Server restored a previous session — hydrate messages
          if (payload.session_id) {
            wsSessionIdRef.current = payload.session_id;
            sessionStorage.setItem(ACTIVE_SESSION_KEY, payload.session_id);
          }
          if (Array.isArray(payload.messages) && payload.messages.length > 0) {
            setMessages(
              payload.messages.map((m) => ({
                sender: m.role === 'user' ? 'user' : 'AI',
                content: m.content,
                isFromHistory: true,
                /* Off `metadata`, which is where the server keeps them. This
                   read used to be `m.artifacts` — a field the restore payload
                   never carried, so it was always null and a restored session
                   lost every card and every record note it had shown. */
                artifacts: m.metadata?.artifacts || null,
                guidedFlow: m.metadata?.guided_flow || null,
                created_at: m.created_at,
              }))
            );
          }
          setIsConnectionOpened(true);
          break;
        }
        case 'chat_response': {
          if (payload.session_id) {
            wsSessionIdRef.current = payload.session_id;
          }
          // The answer landed — there is nothing left to re-attach to.
          pendingTaskRef.current = null;
          if (payload.message) {
            setMessages((prev) => [
              ...prev,
              {
                sender: 'ai',
                content: payload.message,
                messageId: payload.message_id, // For process event correlation
                // Spoken variants of this answer (the agent's __SPEECH__ sections,
                // one per language). Already stripped out of `content` by the
                // backend — kept here so the speaker button can synthesize the one
                // matching whichever language is on screen.
                speechText: payload.speech_text || null,
                englishSpeechText: payload.speech_text_en || null,
                artifacts: payload.metadata?.artifacts || null,
                guidedFlow: payload.metadata?.guided_flow || null,
                created_at: new Date().toISOString(),
              },
            ]);
            setIsTyping(false);
            setIsTypeWriterActive((prev) => prev + 1);
            armPendingSpeech(payload);
            // Same safety net as the typed reply.
            refreshUnrefreshedWrites(payload.message_id, payload.metadata?.function_calls);
          }
          break;
        }
        case 'response_audio': {
          // A message_id means a speaker button requested this clip — hand it to
          // that message so it renders a player. No id means it came from the mic
          // path, which autoplays as it always has.
          const audioMessageId = payload.message_id
            ? normalizeAudioKey(payload.message_id)
            : null;
          if (audioMessageId) {
            setMessageAudio((prev) => ({
              ...prev,
              [audioMessageId]: {
                audioBase64: payload.audio_base64,
                contentType: payload.content_type || 'audio/mpeg',
              },
            }));
            clearPendingAudio(audioMessageId);
          } else {
            playBase64Audio(payload.audio_base64, payload.content_type || 'audio/mpeg');
          }
          break;
        }
        case 'speech_activity':
          if (payload.event === 'begin') {
            setIsSpeaking(true);
          }
          if (payload.event === 'end') {
            setIsSpeaking(false);
            // Delay stop so Google can flush the final transcript before we close the stream
            setTimeout(() => {
              stopRecording(socketRef.current, {
                sessionId: wsSessionIdRef.current,
              });
            }, 500);
          }
          break;
        case 'transcript':
        case 'interim_transcript':
        case 'user_transcript':
          handleTranscriptMessage(payload);
          break;
        case 'warning':
          if (payload.message) {
            console.warn('[chat-model] WebSocket warning:', payload.message);
          }
          break;
        case 'error': {
          // Only speaker-button synthesis attaches a message_id to an error frame.
          // Un-spin that button and stay quiet rather than pushing a failure
          // bubble into the conversation.
          const failedAudioId = payload.message_id
            ? normalizeAudioKey(payload.message_id)
            : null;
          if (failedAudioId) {
            console.warn('[chat-model] Audio synthesis failed:', payload.message);
            clearPendingAudio(failedAudioId);
            break;
          }
          if (payload.message) {
            console.error('[chat-model] WebSocket error:', payload.message);
            setMessages((prev) => [
              ...prev,
              {
                sender: 'system',
                content: payload.message,
                created_at: new Date().toISOString(),
              },
            ]);
          }
          clearActiveProcessState();
          // The turn is over, however it ended — nothing left to re-attach to.
          pendingTaskRef.current = null;
          setIsTyping(false);
          break;
        }
        default:
          break;
      }
    };

    socket.onerror = (event) => {
      console.error('[chat-model] WebSocket encountered an error', event);
      setIsConnectionOpened(false);
      /* Only stop the indicator when nothing is being generated. A dropped
         socket does not stop the agent — the answer is still coming, and the
         reconnect below re-attaches to it. */
      if (!pendingTaskRef.current) setIsTyping(false);
    };

    socket.onclose = (event) => {
      console.warn('[chat-model] WebSocket closed: code=%s reason=%s', event.code, event.reason);
      setIsConnectionOpened(false);
      if (!pendingTaskRef.current) setIsTyping(false);
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      // Auto-reconnect unless intentional close
      if (!intentionalCloseRef.current && retriesRef.current < MAX_RECONNECT_RETRIES) {
        const delay = Math.min(BASE_RECONNECT_DELAY * 2 ** retriesRef.current, MAX_RECONNECT_DELAY);
        const jitter = delay * 0.2 * Math.random();
        retriesRef.current++;
        console.log('[chat-model] Reconnecting in %dms (attempt %d)', Math.round(delay + jitter), retriesRef.current);
        reconnectTimerRef.current = setTimeout(connect, delay + jitter);
      } else if (pendingTaskRef.current) {
        /* No reconnect is coming, so nothing will ever re-attach to the task.
           Holding the indicator past this point promises an answer that cannot
           arrive on this page. */
        console.warn('[chat-model] Giving up on task %s — no reconnect', pendingTaskRef.current.taskId);
        pendingTaskRef.current = null;
        setIsTyping(false);
      }
    };

    }; // end of connect()

    connect();

    return () => {
      intentionalCloseRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      const sock = socketRef.current;
      if (sock) {
        sock.onclose = null; // prevent reconnect on intentional close
        if (sock.readyState === WebSocket.OPEN || sock.readyState === WebSocket.CONNECTING) {
          sock.close();
        }
        socketRef.current = null;
      }
    };
  }, [userId, organizationId, isConsumer]);

  useEffect(() => {
    const handleOnline = () => {
      if (isConnectedRef.current !== true) {
        connectionRef?.current && connectionRef.current();
      }
      setIsOnline(true);
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail?.message;
      const borrowerId = e.detail?.borrowerId;
      const applicationDisplayId = e.detail?.applicationDisplayId;
      if (handleSendMessageRef.current) {
        handleSendMessageRef.current(null, {
          sender: 'user',
          content: msg,
          borrower_id: borrowerId,
          application_display_id: applicationDisplayId,
        });
      }
    };
    window.addEventListener('send-chat-message', handler);
    return () => window.removeEventListener('send-chat-message', handler);
  }, []);

  const currentProcessState = currentMessageId ? processStates[currentMessageId] : null;
  useEffect(() => {
    scrollToBottom();
    // Auto-focus the input when AI finishes responding
    if (!isTyping && chatPromptRef.current) {
      chatPromptRef.current.focus();
    }
  }, [isTypewriterActive, isTyping, messages.length, currentProcessState]);

  const hasFetchedUserData = useRef(false);

  useEffect(() => {
    if (hasFetchedUserData.current) return;
    fetchUserData();
  }, [userProfile]);

  const fetchUserData = async () => {
    if (hasFetchedUserData.current) return;
    hasFetchedUserData.current = true;
    try {
      if (!userProfile?.applicationId) {
        hasFetchedUserData.current = false;
        return;
      }
      if (userProfile?.applicationId) {
        setApplicationId(userProfile.applicationId);
      }
    } catch (error) {
      hasFetchedUserData.current = false; // Allow retry on error
      Sentry.captureException(error);
      console.log(error);
    }
  };

  function covertFormJSON(input) {
    const result = {
      page: '',
      data: {},
    };

    for (const key in input) {
      const [page, field] = key.split('.');
      result.page = page;
      result.data[field] = input[key];
    }

    return result;
  }

  const pageNames = {
    UserProfiles: 'personalInfoData',
    ApplicationPurchaseHomes: 'loanInfoData',
    UserAssets: 'assetData',
    UserEmploymentAndIncomes: 'incomeData',
    UserREOs: 'REOData',
    ApplicationDeclarations: 'declarationData',
    UserDemographics: 'demographicsData',
    UserLiabilities: 'liabilitiesData',
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  const penClicked = async () => {
    setIsNewChat(true);
    setChatHistorySelected(false);
    try {
      const response = await createNewChat({
        borrower_id: borrowerIdValue,
        loan_application_id: applicationId || userProfile?.applicationId,
        metadata: buildChatMetadata(),
      });
      const sessionId = response?.data?.id ?? response?.id ?? null;
      if (sessionId) {
        activateSession(sessionId, { broadcast: true, resetMessages: true });
        await refetchAllChatHistory();
        return;
      }
    } catch (error) {
      console.error('[chat-model] Failed to start new chat', error);
    }
    activateSession(null, { broadcast: true, resetMessages: true });
  };

  // Language toggle for bilingual (non-English) AI replies: ids currently
  // showing the English variant, and ids toggled at least once (those skip the
  // typewriter so switching languages doesn't re-animate).
  const [englishShownIds, setEnglishShownIds] = useState(() => new Set());
  const langToggledIdsRef = useRef(new Set());
  const toggleEnglish = (messageKey) => {
    langToggledIdsRef.current.add(messageKey);
    // Stop whatever is playing — hearing Spanish while reading English is worse
    // than silence. Deactivating pauses the player without dropping its audio,
    // so a click on the other language's speaker starts fresh.
    setActiveAudioId(null);
    setEnglishShownIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageKey)) next.delete(messageKey);
      else next.add(messageKey);
      return next;
    });
  };

  // Switch which version of a regenerated answer is shown.
  const setActiveVersion = (targetMessage, n) => {
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m === targetMessage);
      if (idx < 0 || !prev[idx].versions) return prev;
      const clamped = Math.max(0, Math.min(n, prev[idx].versions.length - 1));
      const updated = [...prev];
      updated[idx] = { ...prev[idx], activeVersion: clamped, content: prev[idx].versions[clamped] };
      return updated;
    });
    // Cached audio is keyed by version, so the clip for the version we just left
    // stays in its own slot — nothing to discard here, and switching back
    // replays it for free.
  };

  // Regenerate an AI answer IN PLACE: re-ask its question (prefixed "Regenerate:"
  // for the agent, but with no visible user bubble) and route the new answer into
  // the same message's version list so the previous output stays in the slider.
  const handleRegenerate = (targetMessage) => {
    const idx = messages.findIndex((m) => m === targetMessage);
    if (idx < 0) return;
    let question = null;
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i]?.sender === 'user') {
        question = messages[i].content;
        break;
      }
    }
    if (!question) return;
    const original = question.replace(/^\s*Regenerate:\s*/i, '');
    handleSendMessage(null, { content: original }, { regenerateTarget: targetMessage });
  };

  const handleSendMessage = async (e, inputValue, opts = {}) => {
    if (e) {
      e.preventDefault();
    }

    const trimmedContent = inputValue?.content?.trim();
    if (!trimmedContent) {
      return;
    }
    if (isTyping) {
      // Block any send while the assistant is still replying
      return;
    }

    if (opts.regenerateTarget) {
      // Regenerate in place: mark the target answer; do NOT add a user bubble
      // (the "Regenerate:" prefix is sent to the agent but never shown).
      regeneratingRef.current = opts.regenerateTarget;
      setRegeneratingMsg(opts.regenerateTarget);
    } else {
      const outgoingMessage = {
        sender: 'user',
        content: trimmedContent,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, outgoingMessage]);
    }
    setIsTypeWriterActive((prev) => prev + 1);
    setIsTyping(true);

    // Persist session to sessionStorage now that the user has actually interacted.
    // This ensures reloads restore the chat only when there was real activity.
    const activeId = wsSessionIdRef.current || chatId.current;
    if (activeId) {
      sessionStorage.setItem(ACTIVE_SESSION_KEY, activeId);
    }

    if (!opts.regenerateTarget) {
      setInputValue({
        sender: 'user',
        content: '',
      });
    }

    const socket = socketRef.current;
    let payload;

    if (isConsumer) {
      // B2C payload: the backend expects message + chat_id
      payload = {
        message: trimmedContent,
        chat_id: wsSessionIdRef.current || null,
      };
      if (inputValue?.metadata && typeof inputValue.metadata === 'object') {
        payload.metadata = inputValue.metadata;
      }
    } else {
      // B2B payload
      payload = {
        type: 'text',
        message: trimmedContent,
        session_id: wsSessionIdRef.current || null,
      };

      const borrowerFromInput = inputValue?.borrower_id || inputValue?.borrowerId || null;
      const loanApplicationFromInput =
        inputValue?.loan_application_id || inputValue?.loanApplicationId || applicationId;
      const applicationDisplayIdFromInput =
        inputValue?.application_display_id ||
        inputValue?.applicationDisplayId ||
        applicationDisplayIdValue ||
        null;

      if (borrowerFromInput) {
        payload.borrower_id = borrowerFromInput;
      }
      if (loanApplicationFromInput) {
        payload.loan_application_id = loanApplicationFromInput;
      }
      if (applicationDisplayIdFromInput) {
        payload.application_display_id = applicationDisplayIdFromInput;
      }
      if (inputValue?.metadata && typeof inputValue.metadata === 'object') {
        payload.metadata = inputValue.metadata;
      }
      /* Which guided flow asked, where a button did rather than the composer.
         The server records it on the turn, so that flow's own surface stands in
         for this answer's prose every time the message is read — not only in
         the session that produced it. */
      if (inputValue?.guided_flow) {
        payload.guided_flow = inputValue.guided_flow;
      }
      if (firstName) {
        payload.user_name = firstName;
      }
      if (userId) {
        payload.user_id = userId;
      }
      if (applicationId) {
        payload.application_id = applicationId;
      }
      if (organizationId) {
        payload.tenant_id = organizationId;
      }
    }
    if (opts.regenerateTarget) {
      // Regenerate in place: tell the backend to append the new answer to the
      // target message's version list instead of creating a new turn.
      payload.type = 'regenerate';
      payload.target_message_id =
        opts.regenerateTarget.messageId || opts.regenerateTarget.id || null;
    }

    const canUseWebSocket =
      socket &&
      socket.readyState === WebSocket.OPEN;

    if (!canUseWebSocket) {
      // Queue the message for when the connection is ready
      console.warn('[chat-model] WebSocket not open — queuing message');
      messageQueueRef.current.push(JSON.stringify(payload));
      return;
    }

    console.log('[chat-model] Sending WebSocket payload:', JSON.stringify(payload, null, 2));

    try {
      socket.send(JSON.stringify(payload));
    } catch (error) {
      console.error('[chat-model] Failed to send message over WebSocket — queuing', error);
      messageQueueRef.current.push(JSON.stringify(payload));
    }
  };
  handleSendMessageRef.current = handleSendMessage;

  const handleFileUpload = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      setIsDocumentUploading(true);
      setChatUploadProgress({
        fileName: selectedFile.name,
        progressPercent: 5,
        status: 'queued',
      });
      try {
        // For consumers (B2C), attach both application and borrower context.
        // For B2B users, keep uploads general (no loan_application_id).
        const payload = {
          file: selectedFile,
          borrowerId: borrowerIdValue,
          title: selectedFile.name,
        };
        if (isConsumer) {
          payload.loanApplicationId = applicationId || userProfile?.applicationId;
          payload.chatId = wsSessionIdRef.current || undefined;
          payload.fileRequestText = (inputValue?.content || '').trim() || 'Please parse this uploaded document.';
          payload.useConsumerChatWorkflow = true;
        } else {
          payload.loanApplicationId = applicationId || undefined;
          payload.chatId = wsSessionIdRef.current || undefined;
          payload.fileRequestText = (inputValue?.content || '').trim() || 'Please parse this uploaded document.';
          payload.useB2BChatWorkflow = true;
        }

        const response = await uploadData(payload);

        // Clear file input
        fileInputRef.current.value = null;

        console.log('chat document upload response', response);
        // Animation stays visible — WebSocket chat_upload_progress / chat_upload_context
        // handlers will hide it once MongoDB storage is complete.
      } catch (e) {
        console.error(e);
        Sentry.captureException(e);
        setIsDocumentUploading(false);
        setChatUploadProgress(null);
      }
    }
  };

  useEffect(() => {
    const processQueuedMessage = async () => {
      if (!chatRequest?.chatMessage) {
        return;
      }

      /* Exactly once, for a queued message that carries an id.
       *
       * The clear below used to be the only guard, and it runs *after* the
       * await — so the queue stayed dirty for the whole round trip, which on a
       * sub-agent question is half a minute. Any re-run of this effect inside
       * that window sent the same message again, and a send that threw left it
       * queued to fire on the next mount. That is how one press of a button
       * became two identical questions minutes apart.
       *
       * The claim is shared rather than local: this panel and the officer one
       * both watch this slice, so a guard either of them kept privately would
       * still let the other send. Messages with no id behave as they always
       * have. */
      if (!claimQueuedRequest(chatRequest.chatMessage.requestId)) return;
      // Cleared before the send, not after: the queue's job is to hand the
      // message over once, and holding it across an await is what replayed it.
      dispatch(setChatMessage(''));

      await handleSendMessage(null, {
        sender: 'user',
        content: chatRequest.chatMessage.message || chatRequest.chatMessage.code || '',
        metadata:
          chatRequest.chatMessage.metadata && typeof chatRequest.chatMessage.metadata === 'object'
            ? chatRequest.chatMessage.metadata
            : undefined,
        loan_application_id: chatRequest.chatMessage.applicationId,
        /* Sent as its own field rather than written into the message, so the
           agent knows which loan without the Profile ID showing up in the
           officer's own words. `handleSendMessage` maps it to
           `application_display_id`, which is the only thing that puts it in the
           agent's context — it is never derived from the application id. */
        application_display_id: chatRequest.chatMessage.applicationDisplayId,
        borrower_id: chatRequest.chatMessage.borrowerId,
        /* Named by whichever button queued this. The server validates it
           against its own allowlist and records it on the turn, so nothing a
           client invents can make a message render as something it is not. */
        guided_flow: chatRequest.chatMessage.guidedFlow,
      });
    };

    processQueuedMessage();
  }, [chatRequest, dispatch]);

  const handleKeyPress = (event) => {
    const text = inputValue.content.trim();

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();

      if (!text) return;
      setIsTyping(false);
      if (chatPromptRef.current) {
        chatPromptRef.current.style.height = 'auto';
      }

      handleSendMessage(null, inputValue);
      setInputValue({ sender: 'user', content: '' });
    }
  };

  if (docked && isTabletViewport && isTabletChatHidden) {
    return (
      <div className={styles.rootWrapper}>
        <button
          type="button"
          className={styles.tabletChatReopen}
          onClick={() => setIsTabletChatHidden(false)}
          aria-label="Reopen chat"
        >
          <PanelLeft className={styles.tabletChatReopenIcon} />
        </button>
      </div>
    );
  }


  return (
    // (applicationId ||
    //   hasPermissions(permissions, 'loanOfficer') ||
    //   hasPermissions(permissions, 'organization') ||
    //   hasPermissions(permissions, 'superAdmin') ||
    //   hasPermissions(permissions, 'branchAdmin') ||
    //   hasPermissions(permissions, 'loanProcessor')) && (
    <div className={styles.rootWrapper}>
      <Resizable
        className={`${styles.container} ${styles.resizableSpacing} ${docked ? `${styles.dockedContainer} m-0 z-[49]` : `ml-2 mr-3 z-[49] ${isMaximized ? 'shadow-xl' : ''}`}`}
        size={{
          width: docked ? '100%' : isMaximized ? `${resizedWidth}vw` : '26vw',
          height: docked ? '100%' : '92vh',
        }}
        onResizeStop={(e, direction, ref, delta) => {
          if (docked) {
            return;
          }
          const viewportWidth = window.innerWidth;
          const modifiedDeltaWidth = (delta.width / viewportWidth) * 100;

          setResizedWidth(
            resizedWidth + modifiedDeltaWidth < 26
              ? 26
              : resizedWidth + modifiedDeltaWidth
          );
        }}
        minWidth={docked ? '100%' : !isMaximized && '26vw'}
        maxWidth={docked ? '100%' : '90vw'}
        enable={{ left: !docked && isMaximized && true }}
      >
        {/* Chat History Button & Sidebar */}
        <ChatHistory
          isMaximized={isMaximized}
          setMessages={setMessages}
          isVectorClicks={isVectorClicks}
          borrowerId={userProfile?.borrowerId}
          applicationId={applicationId || userProfile?.applicationId}
          setVectorClicks={setVectorClicks}
          setIsNewChat={setIsNewChat}
          setNewChatOpens={setNewChatOpens}
          isNewChatOpens={isNewChatOpens}
          chatHistorySelected={chatHistorySelected}
          setChatHistorySelected={setChatHistorySelected}
          setNewChatSelected={setNewChatSelected}
          onActivateSession={activateSession}
          buildChatMetadata={buildChatMetadata}
          setIsHistoryLoading={setIsHistoryLoading}
        />

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button
              className={styles.iconButton}
              onClick={() =>
                setVectorClicks((prev) => {
                  const next = !prev;
                  if (!next) {
                    setNewChatOpens(false);
                    setNewChatSelected(false);
                  }
                  return next;
                })
              }
              aria-label="Toggle chat history"
            >
              <PanelLeft className={styles.headerIcon} />
            </button>
            <img
              className={styles.logoIcon}
              alt="Opus Capital Markets Consultants"
              src="/opus-wordmark.png"
            />
            {/* Logo text hidden - the wordmark already carries the name */}
          </div>

          <div className={styles.iconControls}>
            <button
              className={styles.iconButton}
              onClick={() => penClicked()}
              aria-label="New chat"
            >
              <PenLine className={styles.newChatIcon} />
            </button>
            {docked ? (
              <>
                {isTabletViewport ? (
                  <button
                    className={styles.iconButton}
                    onClick={() => {
                      setVectorClicks(false);
                      setNewChatOpens(false);
                      setNewChatSelected(false);
                      setIsTabletChatHidden(true);
                    }}
                    aria-label="Hide chat panel"
                  >
                    <X className={styles.headerIcon} />
                  </button>
                ) : onClose ? (
                  <button
                    className={styles.iconButton}
                    onClick={onClose}
                    aria-label="Close chat"
                  >
                    <X className={styles.headerIcon} />
                  </button>
                ) : (
                  <button
                    className={styles.iconButton}
                    onClick={() => setIsChatExpanded((prev) => !prev)}
                    aria-label={isChatExpanded ? 'Collapse chat panel' : 'Expand chat panel'}
                  >
                    {isChatExpanded ? (
                      <Minimize className={styles.minimizeIcon} />
                    ) : (
                      <Maximize className={styles.maximizeIcon} />
                    )}
                  </button>
                )}
              </>
            ) : isMaximized ? (
              <button
                className={styles.iconButton}
                onClick={() => {
                  setIsMaximized(false);
                  setResizedWidth(55);
                }}
                aria-label="Minimize"
              >
                <Minimize className={styles.minimizeIcon} />
              </button>
            ) : !docked ? (
              <button
                className={styles.iconButton}
                onClick={() => setIsMaximized(true)}
                aria-label="Maximize"
              >
                <Maximize className={styles.maximizeIcon} />
              </button>
            ) : null}
          </div>
        </div>

        {/* Main Layout - Sidebar + Chat Interface */}
        <div className={styles.mainLayout}>
          {/* Chat Interface */}
          <div className={styles.chatInterface}>
            {isHistoryLoading && messages.length === 0 ? (
              <div className={`${styles.messagesScrollContainer} w-full`}>
                <ChatHistorySkeleton />
              </div>
            ) : messages && messages.length === 0 ? (
              <div
                className={`${shouldShiftForHistory
                  ? 'w-[80%] ml-auto'
                  : 'w-full'
                  } ${styles.emptyState}`}
              />
            ) : (
              <div
                className={`${styles.messagesScrollContainer} w-full`}
                ref={chatContainerRef}
              >
                {messageItems.map((item) => {
                  if (item.type === 'divider') {
                    return <ChatDayDivider key={item.key} label={item.label} />;
                  }
                  const message = item.message;
                  const rawAiContent = message.versions
                    ? message.versions[message.activeVersion ?? 0]
                    : message?.content;
                  const messageParts = parseMessageParts(rawAiContent || '');
                  const messageKey = message.messageId || message.id || item.key;
                  const isEnglishShown =
                    Boolean(messageParts.englishText) && englishShownIds.has(messageKey);
                  const aiDisplayText = isEnglishShown
                    ? messageParts.englishText
                    : messageParts.originalText;
                  /* Does the guided change-of-circumstance card stand in for
                     this reply's prose? True for exactly one message — the
                     answer to the press that opened the card, matched on the id
                     that press minted. Every other message, and this one once
                     the card is closed, renders normally.

                     This used to be "the newest reply", which detached for
                     reasons unrelated to this feature: regeneration versions an
                     answer in place rather than appending it, so one later
                     message left the regenerated answer no longer newest — and
                     any unrelated question asked while the card was open would
                     take it over and lose its own prose.

                     It is now a fact ON the message, written by the server when
                     the turn was answered, so it needs no client state at all
                     and survives a reload and a session opened weeks later.
                     Two presses leave two cards, each naming its own loan,
                     because each turn carries its own. */
                  const playbookLoanId =
                    message.guidedFlow?.kind === 'coc_playbook'
                      ? message.guidedFlow.loan_application_id || null
                      : null;
                  const showsPlaybook = Boolean(playbookLoanId);
                  // Spoken variants for the speaker button. Once a message has
                  // been regenerated its speech is tracked per version, so the
                  // per-version entry wins and we must NOT fall back to the
                  // message-level field (that holds the newest version's speech
                  // and would be read out for an older one). Otherwise: the live
                  // message's own fields, else whatever parseMessageParts
                  // recovered — history rows come back raw with the markers still
                  // embedded.
                  const versionSpeech = message.speechVersions
                    ? message.speechVersions[message.activeVersion ?? 0]
                    : null;
                  const resolvedSpeech = message.speechVersions
                    ? versionSpeech?.speech || messageParts.speechText || null
                    : message.speechText || messageParts.speechText || null;
                  const resolvedEnglishSpeech = message.speechVersions
                    ? versionSpeech?.english || messageParts.englishSpeechText || null
                    : message.englishSpeechText || messageParts.englishSpeechText || null;
                  // The button follows what is on screen: reading English speaks
                  // English. Either side falls back to the other so a message
                  // carrying only one spoken variant still has a working button.
                  // Consumers reach this panel over the B2C socket, which now
                  // answers `synthesize` the same way, so the button works there
                  // too.
                  const speechText = isEnglishShown
                    ? resolvedEnglishSpeech || resolvedSpeech
                    : resolvedSpeech || resolvedEnglishSpeech;
                  const audioKey = audioKeyFor(
                    messageKey,
                    message.activeVersion ?? 0,
                    isEnglishShown,
                  );
                  return message.sender === 'user' ? (
                    <div key={item.key} className={`${messageStyles.messageWrapper} ${messageStyles.user}`}>
                      <div className={messageStyles.messageBubble}>
                        <p className={messageStyles.messageText}>
                          {message.content}
                        </p>
                        <span className="absolute bottom-2 right-3 text-[10px] leading-none text-white/60">
                          {formatMessageTime(message)}
                        </span>
                      </div>
                      {userProfilePic && !isProfilePicError ? (
                        <img
                          src={userProfilePic}
                          alt={firstName || 'User'}
                          className={messageStyles.userAvatarImage}
                          onError={(e) => {
                            console.log('[Chat] Profile picture failed to load, falling back to initials');
                            setIsProfilePicError(true);
                          }}
                        />
                      ) : (
                        <div className={messageStyles.userAvatarCircle}>
                          {initials}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div key={item.key} style={{ marginBottom: '1rem' }}>
                      {message === regeneratingMsg ? (
                        <div className={messageStyles.thinkingContainer}>
                          <div className={messageStyles.thinkingWrapper}>
                            <MiraChevronIcon />
                            <ProcessProgress
                              processState={
                                currentMessageId && processStates[currentMessageId]
                                  ? processStates[currentMessageId]
                                  : { status: 'active', steps: [{ id: 'thinking', label: 'Regenerating…', icon: 'brain', status: 'active', index: 0 }] }
                              }
                              messageId={currentMessageId}
                            />
                          </div>
                        </div>
                      ) : showsPlaybook ? (
                        /* This ONE reply renders as the guided card instead of
                           its own prose. The card is the answer; a paragraph
                           beside it is a second answer to one question, and the
                           model has no way to know the card is there.

                           The turn itself still happens — its register read is
                           what fills in each record's narrative and prong — and
                           closing the card brings the prose straight back,
                           because this branch is the only thing suppressing it.
                           No other message is affected by any of this. */
                        <div className={`${messageStyles.messageWrapper} ${messageStyles.ai}`}>
                          <MiraChevronIcon />
                          <div className="flex-1 min-w-0">
                            {/* No close button. The card is this turn's answer,
                                recorded on the message — dismissing it could
                                only reveal the prose underneath, which is a
                                paragraph written against a register that has
                                since moved. A dismissal that did not persist
                                would also come straight back on the next
                                reload, which is the behaviour this change
                                exists to remove. */}
                            <CocPlaybook
                              loanId={playbookLoanId}
                              artifact={cocArtifactOf(message)}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className={`${messageStyles.messageWrapper} ${messageStyles.ai}`}>
                          <MiraChevronIcon />
                          <div className={messageStyles.messageBubble}>
                            <div className={messageStyles.messageText}>
                              <WordByWord
                                text={aiDisplayText}
                                signalKey={messageParts.originalText}
                                delay={5}
                                setIsTypeWriterActive={setIsTypeWriterActive}
                                setTyping={() => {
                                  setIsTyping(false);
                                }}
                                skipAnimation={!!message.isFromHistory || langToggledIdsRef.current.has(messageKey)}
                              />
                            </div>
                            <span className="block text-right text-[10px] leading-none mt-1 text-gray-400">
                              {formatMessageTime(message)}
                            </span>
                          </div>
                        </div>
                      )}
                      {/* No copy, thumbs, regenerate or read-aloud on the one
                          reply whose prose is standing down for the card —
                          they would all act on text that is not on screen. */}
                      {message.sender !== 'system' && message !== regeneratingMsg
                        && !showsPlaybook && (
                        <div style={{ paddingLeft: '30px' }}>
                          {message.versions && message.versions.length > 1 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px', color: '#8a93a6', fontSize: '12px' }}>
                              <button
                                type="button"
                                onClick={() => setActiveVersion(message, (message.activeVersion ?? 0) - 1)}
                                disabled={(message.activeVersion ?? 0) <= 0}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '0 2px', opacity: (message.activeVersion ?? 0) <= 0 ? 0.4 : 1 }}
                                aria-label="Previous version"
                              >
                                ‹
                              </button>
                              <span>{(message.activeVersion ?? 0) + 1}/{message.versions.length}</span>
                              <button
                                type="button"
                                onClick={() => setActiveVersion(message, (message.activeVersion ?? 0) + 1)}
                                disabled={(message.activeVersion ?? 0) >= message.versions.length - 1}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '0 2px', opacity: (message.activeVersion ?? 0) >= message.versions.length - 1 ? 0.4 : 1 }}
                                aria-label="Next version"
                              >
                                ›
                              </button>
                            </div>
                          )}
                          <MessageActions
                            content={aiDisplayText}
                            messageId={message.messageId || message.id}
                            sessionId={chatId.current}
                            initialFeedback={message.feedback ?? null}
                            isB2C={isConsumer}
                            consumerId={isConsumer ? String(userId) : undefined}
                            clientOrgId={isConsumer && organizationId ? String(organizationId) : undefined}
                            onRegenerate={() => handleRegenerate(message)}
                            hasEnglish={Boolean(messageParts.englishText)}
                            englishShown={isEnglishShown}
                            onToggleLanguage={() => toggleEnglish(messageKey)}
                            speechText={speechText}
                            onPlaySpeech={() => handlePlaySpeech(audioKey, speechText)}
                            isSpeechLoading={Boolean(pendingAudioIds[audioKey])}
                            isSpeechActive={activeAudioId === audioKey}
                          />
                          {messageAudio[audioKey] && (
                            <AudioPlayer
                              audioBase64={messageAudio[audioKey].audioBase64}
                              mimeType={messageAudio[audioKey].contentType}
                              playToken={audioPlayTokens[audioKey] || 0}
                              isActive={activeAudioId === audioKey}
                              onPlay={() => setActiveAudioId(audioKey)}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* AI Transparency: Show inside the last AI message or as thinking state */}
                {isTyping && !regeneratingMsg && currentMessageId && processStates[currentMessageId] && (
                  <div className={messageStyles.thinkingContainer}>
                    <div className={messageStyles.thinkingWrapper}>
                      <MiraChevronIcon />
                      <ProcessProgress
                        processState={processStates[currentMessageId]}
                        messageId={currentMessageId}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            {isDocumentUploading && (
              <div className="flex gap-2 justify-center items-center py-2">
                <Loader variant="button" message="Uploading document..." />
              </div>
            )}
            <form
              className={`${styles.composerForm} w-full`}
            >
              <div
                className={styles.composerShell}
              >
                <div className={styles.composerInputArea}>
                  {isRecording ? (
                    <div className="w-full">
                      <div className="flex items-center gap-2">
                        <VoiceLevelIndicator
                          audioContext={audioContext}
                          mediaStream={mediaStream}
                          isRecording={isRecording}
                        />
                        <textarea
                          value={interimTranscript || transcriptText || "Listening..."}
                          readOnly
                          className={`${inputStyles.textareaRecording} ${interimTranscript ? inputStyles.interim : inputStyles.final} w-full resize-none ml-2 outline-none overflow-hidden overflow-y-scroll hiddenScroll`}
                          rows={1}
                        />
                      </div>
                      {transcriptText && !isTyping && (
                        <button
                          onClick={handleSendTranscript}
                          className="text-primary text-sm font-medium bg-custom-primary100 hover:bg-custom-primary200 px-2 py-1 rounded ml-2 mt-1"
                        >
                          Send
                        </button>
                      )}
                    </div>
                  ) : (
                    <textarea
                      type="text"
                      disabled={isTyping}
                      value={inputValue.content}
                      onChange={handleInputChange}
                      ref={chatPromptRef}
                      placeholder="Ask Mira AI anything..."
                      className={`${styles.composerTextarea} resize-none outline-none overflow-hidden overflow-y-auto hiddenScroll`}
                      onKeyDown={handleKeyPress}
                      rows={1}
                    />
                  )}
                </div>
                <div className={styles.composerActionsGroup}>
                  <div className={styles.composerAction}>
                    <button
                      type="button"
                      onClick={handleToggleRecording}
                      disabled={isTyping && !isRecording}
                      aria-disabled={isTyping && !isRecording}
                      title={isTyping && !isRecording ? 'Wait for the assistant to finish replying' : undefined}
                      style={isTyping && !isRecording ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                      className={`mic-button ${isRecording ? 'recording' : ''} ${isSpeaking ? 'speaking' : ''}`}
                    >
                      {isRecording ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M5 3.5h6A1.5 1.5 0 0 1 12.5 5v6a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 11V5A1.5 1.5 0 0 1 5 3.5z" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5z" />
                          <path d="M10 8a2 2 0 1 1-4 0V3a2 2 0 1 1 4 0v5zM8 0a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V3a3 3 0 0 0-3-3z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className={styles.composerAction}>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className={inputStyles.fileInput}
                    />
                    <img
                      src={selectDocumentIcon}
                      alt="Select Document"
                      className={styles.attachmentIcon}
                      onClick={!isTyping ? handleFileUpload : undefined}
                      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isTyping) handleFileUpload(e); }}
                      role="button" // eslint-disable-line jsx-a11y/no-noninteractive-element-to-interactive-role
                      tabIndex={0}
                    />
                  </div>
                  <button
                    type="button"
                    aria-label="Send message"
                    onClick={(e) => {
                      if (isRecording) {
                        e.preventDefault();
                        handleSendTranscript();
                        return;
                      }

                      if (isTyping) {
                        e.preventDefault();
                        return;
                      }

                      if (chatPromptRef.current) {
                        chatPromptRef.current.style.height = 'auto';
                      }
                      handleSendMessage(e, inputValue);
                    }}
                    disabled={
                      isLoading ||
                      isMessageStreaming ||
                      !isConnectionOpen ||
                      isTyping ||
                      (isRecording && !transcriptText && !interimTranscript) ||
                      (!isRecording && !inputValue.content)
                    }
                    className={`${styles.sendAction} ${isLoading ||
                      isMessageStreaming ||
                      !isConnectionOpen ||
                      isTyping ||
                      (isRecording && !transcriptText && !interimTranscript) ||
                      (!isRecording && !inputValue.content)
                      ? styles.sendActionDisabled
                      : styles.sendActionEnabled
                      }`}
                  >
                    {isTyping ? (
                      <Loader variant="button" />
                    ) : (
                      <ArrowUp className={styles.sendIcon} />
                    )}
                  </button>
                </div>
              </div>
            </form>
            {speechError && <p className={`error-message ${inputStyles.errorMessage}`}>{speechError}</p>}
            {/* No mark here. The assistant's own avatar carries it on each
                reply, which is where it means something; a second copy in the
                footer only competes with the Opus wordmark in the header. */}
            <div className={styles.disclaimerBlock}>
              <p className={styles.disclaimerTitle}>Powered by MIRA</p>
              <p className={styles.disclaimerText}>
                Mira can make mistakes. Verify important info.
              </p>
            </div>
          </div> {/* End chatInterface */}
        </div> {/* End mainLayout */}
      </Resizable>
    </div>
  )
  // );
};

export default ChatModel;
